<ng-container *ngIf="paymentPlanState() as state">
  <section
    *ngIf="state !== 'hidden'"
    class="balance-payment-plan-panel"
    [attr.data-state]="state"
    fxLayout="column"
    fxLayoutGap="1rem"
  >
    <header class="balance-payment-plan-panel__header">
      <h3>{{ 'balance.payment-plan.heading' | translate }}</h3>
    </header>

    <mat-progress-bar *ngIf="submitting()" mode="indeterminate"></mat-progress-bar>

    <ng-container [ngSwitch]="state">
      <ng-container *ngSwitchCase="'has-plan'">
        <div fxLayout="column" fxLayoutGap="1rem">
          <mmx-balance-payment-plan-summary [balance]="balance()"></mmx-balance-payment-plan-summary>

          <mat-divider class="balance-payment-plan-panel__divider"></mat-divider>

          <footer
            class="balance-payment-plan-panel__footer"
            fxLayout="row"
            fxLayoutAlign="end center"
          >
            <button
              mat-stroked-button
              color="warn"
              type="button"
              data-testid="payment-plan-cancel"
              [disabled]="submitting()"
              (click)="onCancel()"
            >
              <mat-icon>cancel</mat-icon>
              <span>{{ 'balance.payment-plan.cancel' | translate }}</span>
            </button>
          </footer>
        </div>
      </ng-container>

      <ng-container *ngSwitchCase="'available'">
        <form
          [formGroup]="form"
          (ngSubmit)="submit()"
          class="balance-payment-plan-panel__form"
          data-testid="payment-plan-form"
          fxLayout="column"
          fxLayoutGap="1rem"
        >
          <div
            fxLayout="row"
            fxLayout.lt-sm="column"
            fxLayoutGap="1rem"
            fxLayoutAlign="space-between center"
          >
            <mmx-saved-card-select
              fxFlex
              formControlName="paymentMethodId"
              [patientId]="patientId()"
              [allowNewCard]="allowNewCard()"
              [required]="true"
              label="balance.payment-plan.saved-card"
              requiredErrorKey="balance.payment-plan.card-required"
              testId="payment-plan-card-select"
              (cardsLoaded)="onSavedCardsLoaded($event)"
            ></mmx-saved-card-select>

            <mat-form-field appearance="fill" fxFlex>
              <mat-label>{{ 'balance.payment-plan.months' | translate }}</mat-label>
              <mat-select formControlName="months" data-testid="payment-plan-months">
                <mat-option *ngFor="let m of monthOptions()" [value]="m">{{ m }}</mat-option>
              </mat-select>
              <mat-hint *ngIf="maxLoanTerm() > 0">
                {{ 'balance.payment-plan.max-loan-term-hint' | translate: { months: maxLoanTerm() } }}
              </mat-hint>
              <mat-error *ngIf="form.controls.months.hasError('required')">
                {{ 'balance.payment-plan.months-invalid' | translate }}
              </mat-error>
            </mat-form-field>
          </div>

          <mat-form-field appearance="fill">
            <mat-label>{{ 'balance.payment-plan.first-payment' | translate }}</mat-label>
            <span matTextPrefix>$&nbsp;</span>
            <input
              matInput
              type="number"
              step="0.01"
              [min]="minUpfrontPayment() > 0 ? minUpfrontPayment() : 0.01"
              formControlName="firstPayment"
              data-testid="payment-plan-first-payment"
            />
            <mat-hint *ngIf="minUpfrontPayment() > 0">
              {{ 'balance.payment-plan.min-upfront-hint' | translate: { amount: (minUpfrontPayment() | currency:'USD') } }}
            </mat-hint>
            <mat-error *ngIf="form.controls.firstPayment.hasError('min') || form.controls.firstPayment.hasError('required')">
              {{ 'balance.payment-plan.first-payment-invalid' | translate }}
            </mat-error>
          </mat-form-field>

          <mat-divider></mat-divider>

          <div
            class="balance-payment-plan-panel__derived"
            data-testid="payment-plan-monthly-amount"
            fxLayout="row"
            fxLayoutAlign="center center"
            fxLayoutGap=".5rem"
          >
            <ng-container *ngIf="hasDistinctFirstPayment(); else uniformSummary">
              <strong data-testid="payment-plan-derived-first">{{ firstPaymentValue() | currency:'USD' }}</strong>
              <span>{{ 'balance.payment-plan.today' | translate }}</span>
              <span>+</span>
              <span data-testid="payment-plan-derived-installments">{{ scheduledInstallmentCount() }} × </span>
              <strong>{{ monthlyAmount() | currency:'USD' }}</strong>
              <span>/ {{ 'common.month' | translate }}</span>
            </ng-container>
            <ng-template #uniformSummary>
              <span data-testid="payment-plan-derived-installments">{{ form.controls.months.value }} × </span>
              <strong>{{ monthlyAmount() | currency:'USD' }}</strong>
              <span>/ {{ 'common.month' | translate }}</span>
            </ng-template>
          </div>

          <div
            *ngIf="error()"
            class="balance-payment-plan-panel__error"
            data-testid="payment-plan-error"
            fxLayout="row"
            fxLayoutAlign="center center"
          >
            {{ error()! | translate }}
          </div>

          <button
            mat-flat-button
            color="primary"
            type="submit"
            data-testid="payment-plan-submit"
            [disabled]="submitting() || form.invalid"
          >
            {{ 'balance.payment-plan.create' | translate }}
          </button>
        </form>
      </ng-container>

      <ng-container *ngSwitchDefault>
        <div
          class="balance-payment-plan-panel__unavailable"
          data-testid="payment-plan-unavailable"
          fxLayout="column"
          fxLayoutAlign="center center"
          fxLayoutGap=".5rem"
        >
          <mat-icon class="balance-payment-plan-panel__unavailable-icon" color="primary">info</mat-icon>
          <p><strong>{{ 'balance.payment-plan.unavailable' | translate }}</strong></p>
          <p *ngIf="state === 'disabled'">{{ 'balance.payment-plan.disabled-reason' | translate }}</p>
          <p *ngIf="state === 'amount-too-low'">
            {{ 'balance.payment-plan.amount-too-low' | translate: { minAmount: (minLoanAmount() | currency:'USD') } }}
          </p>
        </div>
      </ng-container>
    </ng-container>
  </section>
</ng-container>
